import yaml from "js-yaml";
import { buildJSONRequest } from "../RequestBuilder";
import { getRoot } from "../../../../../abstract/redux/slice/structureSlice";
import store from "../../../../../abstract/redux/store";
import { exportToYaml } from "../../../../../abstract/controller/modelYamlExport";

export const STORAGE_KEYS = {
  MESSAGES: "aiChatMessages",
  LAST_MESSAGE: "aiChatLastMessage",
  ATTACHED_FILES: "aiChatAttachedFiles",
  YAML_SESSIONS: "yamlSessions",
  LLM_PROVIDER: "provider"
};

export const MODEL_STATUS = {
  NORMAL: "normal",
  APPROVED: "approved",
  REJECTED: "rejected",
  PENDING: "pending",
};

export const PROVIDERS = ["OpenAI", "Gemini", "Claude", "Ollama"]

export function isYamlEqual(yaml1, yaml2) {
  try {
    const obj1 = yaml.load(yaml1);
    const obj2 = yaml.load(yaml2);
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  } catch (e) {
    return false;
  }
}

export const isValidYAML = (str) => {
  if (!str || typeof str !== "string" || str.trim() === "") {
    return false;
  }

  try {
    const parsed = yaml.load(str.trim());
    return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed);
  } catch (e) {
    return false;
  }
};

export function getLatestApprovedModel() {
  try {
    const sessions = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.YAML_SESSIONS)) || [];
    return sessions.length > 0 ? sessions[sessions.length - 1] : null;
  } catch (e) {
    return null;
  }
}

export function getModelStatus() {
  try {
    const sessions = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.YAML_SESSIONS)) || [];
    if (sessions.length === 0) {
      return MODEL_STATUS.NORMAL;
    }
    const latestSession = sessions[sessions.length - 1];
    return latestSession.modelStatus || MODEL_STATUS.NORMAL;
  } catch (e) {
    return MODEL_STATUS.NORMAL;
  }
}

export function updateModelStatus(status) {
  try {
    const sessions = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.YAML_SESSIONS)) || [];

    if (sessions.length === 0) {
      return;
    }

    const latestIndex = sessions.length - 1;
    const latestSession = sessions[latestIndex];

    latestSession.modelStatus = status;
    sessions[latestIndex] = latestSession;

    sessionStorage.setItem(STORAGE_KEYS.YAML_SESSIONS, JSON.stringify(sessions));
  } catch (e) {}
}

export function resetModelStatus() {
  try {
    const sessions = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.YAML_SESSIONS)) || [];

    if (sessions.length === 0) {
      return;
    }

    const latestIndex = sessions.length - 1;
    const latestSession = sessions[latestIndex];

    latestSession.modelStatus = MODEL_STATUS.NORMAL;
    sessions[latestIndex] = latestSession;

    sessionStorage.setItem(STORAGE_KEYS.YAML_SESSIONS, JSON.stringify(sessions));
  } catch (e) {}
}

export const normalizeModelToYAML = (model) => {
  if (!model) return null;
  if (typeof model === "string") return model;
  if (typeof model === "object") {
    try {
      return yaml.dump(model);
    } catch (e) {
      return String(model);
    }
  }
  return String(model);
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const BYTES_PER_KB = parseInt(process.env.BYTES_PER_KB, 10) || 1024;
  const units = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(BYTES_PER_KB));
  return parseFloat((bytes / Math.pow(BYTES_PER_KB, i)).toFixed(2)) + " " + units[i];
};

export const processMessageContent = (message) => {
  return [
    {
      type: "text",
      content: message,
    },
  ];
};

function processUserMessage(message, yaml, files) {

  return {
    id: new Date(),
    type: 'user',
    text: message,
    model: yaml || null,
    fileNames: files.map(f => f.name),
  };
}

function processBotResponse(data, files) {

  return {
    id: new Date(),
    thread: data.id,
    type: 'bot',
    text: data.message,
    model: data.model || null,
    hasValidModel: !!data.model,
    fileNames: files && files.length > 0 ? files.map(f => f.name) : [],
  };
}

function normalizeFilesInput(files) {
  if (files === null || files === undefined) {
    return [];
  }

  if (Array.isArray(files)) {
    return files;
  }

  if (files instanceof FileList) {
    return Array.from(files);
  }

  if (files instanceof File) {
    return [files];
  }

  return [files];
}

async function parseResponseText(response) {
  const responseText = await response.text();

  try {
    return JSON.parse(responseText);
  } catch (parseError) {}
}

//---------------------------------------------------------------------------

function getYamlFromStore() {
  const _nodeRoot = getRoot(store.getState());

  if (_nodeRoot) {
    return _nodeRoot ? exportToYaml(_nodeRoot) : "";
  }

  return "YAML";
}

export const createSendMessageToAPI = (setIsLoading, endpoint, setMessages) => {

  return async (message, files = null) => {
    setIsLoading(true);

    try {
      const yaml = getYamlFromStore();
      const history = sessionStorage.getItem(STORAGE_KEYS.MESSAGES) || "[]";
      
      const fileArray = normalizeFilesInput(files);

      const request = await buildJSONRequest(message, yaml, history, fileArray);
      const userMessage = processUserMessage(message, yaml, files);

      setMessages(prev => [...prev, userMessage]);

      const response = await fetch(endpoint, request);

      resetModelStatus();

      const data = await parseResponseText(response);

      if (data.model) {
        const sessions = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.YAML_SESSIONS)) || [];
        const latestIndex = sessions.length - 1;
        if (latestIndex >= 0) {
          sessions[latestIndex].modelStatus = MODEL_STATUS.PENDING;
          sessionStorage.setItem(STORAGE_KEYS.YAML_SESSIONS, JSON.stringify(sessions));
        }
      }

      const botResponse = processBotResponse(data, fileArray);

      setMessages(prev => [...prev, botResponse]);

      return {
        user: processUserMessage(message, yaml, files),
        bot: processBotResponse(data, fileArray)
      }
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };
};

//---------------------------------------------------------------------------

export const getProviderLLM = () => {
  return sessionStorage.getItem(STORAGE_KEYS.LLM_PROVIDER) || PROVIDERS[0];
}

export const setProviderLLM = (provider) => {
  sessionStorage.setItem(STORAGE_KEYS.LLM_PROVIDER, provider);
}